import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./Preloader.module.css";
import logoImage from "../assets/logo.png";

const PreloaderContext = createContext();

export const usePreloader = () => useContext(PreloaderContext);

export const PreloaderProvider = ({ children }) => {
  const [loading, setLoading] = useState(true); // Always start true for a clean entry
  const [videoLoaded, setVideoLoaded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // This function handles the "reveal" once everything is ready
    const handleAllAssetsLoaded = () => {
      // Intentional delay (800ms) creates a premium, deliberate reveal effect
      setTimeout(() => {
        setLoading(false);
      }, 800);
    };

    // 1. Logic for Initial Site Visit
    if (document.readyState === "complete") {
      // If the browser finished before this code mounted
      if (location.pathname === "/") {
        // On Homepage, we still wait for the Hero Video specifically
        if (videoLoaded) handleAllAssetsLoaded();
      } else {
        handleAllAssetsLoaded();
      }
    } else {
      /**
       * ⚡ THE KEY CHANGE:
       * window.addEventListener('load') waits for ALL images, frames, and objects.
       */
      window.addEventListener("load", handleAllAssetsLoaded);

      // Fallback: If on homepage, ensure videoLoaded is also required
      if (location.pathname === "/" && !videoLoaded) {
        window.removeEventListener("load", handleAllAssetsLoaded);
      }

      return () => window.removeEventListener("load", handleAllAssetsLoaded);
    }
  }, [videoLoaded, location.pathname]);

  // 2. Logic for Route Transitions (Internal Navigation)
  useEffect(() => {
    if (loading) {
      // Standard transition timing for navigating between internal pages
      const timer = setTimeout(() => {
        setLoading(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [location, loading]);

  const triggerTransition = (path) => {
    setLoading(true);
    // Give the black curtain 400ms to cover the screen before the route changes
    setTimeout(() => {
      navigate(path);
    }, 400);
  };

  return (
    <PreloaderContext.Provider value={{ triggerTransition, setVideoLoaded }}>
      {children}
      {/* The Preloader UI */}
      <div className={`${styles.preloader} ${loading ? styles.active : ""}`}>
        <div className={styles.content}>
          <img src={logoImage} alt="Dukamo Logo" className={styles.logo} />
          <div className={styles.line}></div>
        </div>
      </div>
    </PreloaderContext.Provider>
  );
};
